import type { JSX } from 'react';
import { History, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Pill } from '@/components/ui/pill';
import {
  DataTable,
  DataTableCell,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { useJobsList, useSearchHistory, useStopScrape } from '../hooks/scrape';
import { elapsedStr } from '../screens/ActiveJobs';
import type { JobSummary, SearchHistoryEntry } from '../api/types';
import { formatDate } from '../components/lead-table/rows';
import { PLATFORM_LABELS, toPlatform } from '../lib/platform';
import { SearchJobInsights } from '../search/JobInsights';
import { SoundList, SoundText } from '../components/SoundLink';
import { soundRefsFromParams } from '../lib/sounds';

/**
 * This folder's searches: what's running or ran recently, then every search
 * recorded against it. Replaces the global recent-searches strip.
 */
export function FolderActivity({
  folderPublicId,
  folderName,
}: {
  folderPublicId: string;
  folderName?: string;
}): JSX.Element {
  const jobs = (useJobsList().data ?? []).filter((j) => j.folderId === folderPublicId);
  const history = useSearchHistory(folderPublicId);
  const entries = history.data ?? [];
  const running = jobs.filter((j) => j.status === 'running');
  const recent = jobs.filter((j) => j.status !== 'running');

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <h2 className="text-[15px] font-bold">Searches</h2>
        {jobs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No searches are running or finished recently in this folder.</p>
        ) : (
          <DataTable aria-label="Searches">
            <DataTableHeader>
              <tr>
                <DataTableHeaderCell>Search</DataTableHeaderCell>
                <DataTableHeaderCell>Status</DataTableHeaderCell>
                <DataTableHeaderCell numeric>Added</DataTableHeaderCell>
                <DataTableHeaderCell numeric>Checked</DataTableHeaderCell>
                <DataTableHeaderCell>Started</DataTableHeaderCell>
                <DataTableHeaderCell>Took</DataTableHeaderCell>
                <DataTableHeaderCell narrow>
                  <span className="sr-only">Actions</span>
                </DataTableHeaderCell>
              </tr>
            </DataTableHeader>
            <tbody>
              {[...running, ...recent].map((j) => (
                <JobRow key={j.id} job={j} />
              ))}
            </tbody>
          </DataTable>
        )}
      </section>

      {recent[0] && (
        <section className="space-y-2">
          <h2 className="text-[15px] font-bold">Latest search: other folders and close calls</h2>
          <SearchJobInsights jobId={recent[0].id} folderPublicId={folderPublicId} folderName={folderName} />
        </section>
      )}

      <section className="space-y-2">
        <h2 className="text-[15px] font-bold">Search history</h2>
        {entries.length === 0 ? (
          <div className="table-frame">
            <EmptyState
              icon={<History className="h-6 w-6" strokeWidth={1.5} />}
              title="No searches recorded yet"
              hint="Searches run from the Find creators tab show up here."
            />
          </div>
        ) : (
          <ul className="panel divide-y divide-border rounded-xl">
            {entries.map((e) => (
              <li key={e.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-4 py-2.5 text-sm">
                <span className="min-w-0 flex-1 truncate font-medium">
                  <SearchLine entry={e} />
                </span>
                <span className="text-muted-foreground">{formatDate(e.ranAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function JobRow({ job }: { job: JobSummary }): JSX.Element {
  const stop = useStopScrape();
  const isRunning = job.status === 'running';
  const stopping = stop.variables === job.id && (stop.isPending || stop.isSuccess);
  return (
    <DataTableRow>
      <DataTableCell anchor data-label="Search">
        <span className="block max-w-[20rem] truncate" title={job.keywords}>
          <SoundText text={job.keywords || 'Search'} />
        </span>
      </DataTableCell>
      <DataTableCell data-label="Status">
        {isRunning ? (
          <Pill tone="info">Running</Pill>
        ) : job.status === 'error' ? (
          <Pill tone="failed">Failed</Pill>
        ) : (
          <Pill tone="success">Done</Pill>
        )}
      </DataTableCell>
      <DataTableCell numeric data-label="Added">
        {job.saved}
        {job.targetCount > 0 && <span className="text-muted-foreground">/{job.targetCount}</span>}
      </DataTableCell>
      <DataTableCell numeric data-label="Checked">
        {job.checked}
      </DataTableCell>
      <DataTableCell muted data-label="Started">
        {formatDate(job.startedAt)}
      </DataTableCell>
      <DataTableCell muted data-label="Took">
        {elapsedStr(job.startedAt, job.finishedAt)}
      </DataTableCell>
      <DataTableCell actions>
        {isRunning && (
          <Button size="sm" variant="ghost" disabled={stopping} onClick={() => stop.mutate(job.id)}>
            <Square className="h-2.5 w-2.5 fill-current" />
            {stopping ? 'Stopping…' : 'Stop'}
          </Button>
        )}
      </DataTableCell>
    </DataTableRow>
  );
}

/** One line for a recorded search: platform, mode and what was searched for. */
export function describeSearch(e: Pick<SearchHistoryEntry, 'params'>): string {
  const p = e.params ?? {};
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  // A sound search names its sounds, never its topic words (which ride in
  // keywordsList) — and Instagram audio carries only musicIds.
  if (p.searchMode === 'sound') {
    const ids = soundRefsFromParams(p).map((r) => r.id);
    return `${PLATFORM_LABELS[toPlatform(p.platform)]} sound${ids.length ? `: ${ids.join(', ')}` : ''}`;
  }
  const list = (v: unknown) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string').join(', ') : '');
  const what =
    list(p.keywordsList) ||
    str(p.keywords) ||
    list(p.hashtags).replace(/(^|, )/g, '$1#') ||
    list(p.seedUsernames).replace(/(^|, )/g, '$1@') ||
    list(p.usernames).replace(/(^|, )/g, '$1@') ||
    (str(p.musicId) ? 'a sound' : '');
  const mode = str(p.searchMode) || 'keyword';
  const label = PLATFORM_LABELS[toPlatform(p.platform)];
  return `${label} ${mode}${what ? `: ${what}` : ''}`;
}

/** {@link describeSearch}, with a sound search's sounds as links. */
function SearchLine({ entry }: { entry: Pick<SearchHistoryEntry, 'params'> }): JSX.Element {
  const p = entry.params ?? {};
  const sounds = soundRefsFromParams(p);
  if (sounds.length === 0) return <>{describeSearch(entry)}</>;
  return (
    <>
      {PLATFORM_LABELS[toPlatform(p.platform)]} sound: <SoundList sounds={sounds} />
    </>
  );
}
